import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Award, Calendar, User, FileText } from 'lucide-react';

export default function PromotionHistoryModal({ contractor, onClose }) {
    const promotionHistory = contractor.promotionHistory || [];

    const getPhaseInfo = (phase) => {
        const phases = {
            'shadow': { name: 'Shadow', color: 'gray' },
            'trainee': { name: 'Trainee', color: 'blue' },
            'junior': { name: 'Junior Tech', color: 'purple' },
            'standard': { name: 'Standard Tech', color: 'green' },
            'experienced': { name: 'Experienced Tech', color: 'yellow' }
        };
        return phases[phase] || { name: phase, color: 'gray' };
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                <Award className="w-6 h-6 text-purple-600" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    Promotion History
                                </h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    {contractor.name}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-88px)]">
                        {promotionHistory.length > 0 ? (
                            <div className="space-y-4">
                                {/* Timeline */}
                                <div className="relative">
                                    {/* Vertical line */}
                                    <div className="absolute left-8 top-8 bottom-8 w-0.5 bg-gray-200"></div>

                                    {/* Promotion entries */}
                                    {promotionHistory.map((promotion, index) => {
                                        const fromPhase = getPhaseInfo(promotion.from);
                                        const toPhase = getPhaseInfo(promotion.to);
                                        const isLatest = index === promotionHistory.length - 1;

                                        return (
                                            <motion.div
                                                key={index}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: index * 0.1 }}
                                                className="relative flex items-start space-x-4 pb-8 last:pb-0"
                                            >
                                                {/* Icon */}
                                                <div className={`relative z-10 w-16 h-16 rounded-full bg-${toPhase.color}-100 flex items-center justify-center flex-shrink-0 ring-4 ring-white`}>
                                                    <Award className={`w-8 h-8 text-${toPhase.color}-600`} />
                                                </div>

                                                {/* Content */}
                                                <div className="flex-1 bg-gray-50 rounded-lg p-4 mt-2">
                                                    {/* Header */}
                                                    <div className="flex items-start justify-between mb-3">
                                                        <div>
                                                            <div className="flex items-center space-x-2 mb-1">
                                                                <span className={`px-3 py-1 rounded-full text-sm font-medium bg-${fromPhase.color}-100 text-${fromPhase.color}-800`}>
                                                                    {fromPhase.name}
                                                                </span>
                                                                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                                                </svg>
                                                                <span className={`px-3 py-1 rounded-full text-sm font-medium bg-${toPhase.color}-100 text-${toPhase.color}-800`}>
                                                                    {toPhase.name}
                                                                </span>
                                                            </div>
                                                            {isLatest && (
                                                                <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                                                                    Current Phase
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Date */}
                                                    <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                                                        <Calendar className="w-4 h-4" />
                                                        <span>
                                                            {new Date(promotion.date).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric'
                                                            })}
                                                        </span>
                                                    </div>

                                                    {/* Admin Note */}
                                                    {promotion.adminNote && (
                                                        <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                                                            <div className="flex items-start space-x-2">
                                                                <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                                <p className="text-sm text-gray-700 italic">
                                                                    "{promotion.adminNote}"
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Metrics at time of promotion */}
                                                    {promotion.metrics && (
                                                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                                                            {promotion.metrics.totalJobs !== undefined && (
                                                                <div className="bg-white rounded p-2">
                                                                    <span className="text-gray-500">Jobs:</span>
                                                                    <span className="ml-1 font-semibold text-gray-900">
                                                                        {promotion.metrics.totalJobs}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {promotion.metrics.completionRate !== undefined && (
                                                                <div className="bg-white rounded p-2">
                                                                    <span className="text-gray-500">Completion:</span>
                                                                    <span className="ml-1 font-semibold text-gray-900">
                                                                        {promotion.metrics.completionRate}%
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {promotion.metrics.averageRating !== undefined && (
                                                                <div className="bg-white rounded p-2">
                                                                    <span className="text-gray-500">Rating:</span>
                                                                    <span className="ml-1 font-semibold text-gray-900">
                                                                        {promotion.metrics.averageRating.toFixed(1)}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {promotion.metrics.daysActive !== undefined && (
                                                                <div className="bg-white rounded p-2">
                                                                    <span className="text-gray-500">Days Active:</span>
                                                                    <span className="ml-1 font-semibold text-gray-900">
                                                                        {promotion.metrics.daysActive}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    No Promotion History
                                </h3>
                                <p className="text-gray-600">
                                    This contractor hasn't been promoted yet.
                                </p>
                            </div>
                        )}

                        {/* Current Status Summary */}
                        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h3 className="font-semibold text-blue-900 mb-2">Current Status</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                <div>
                                    <p className="text-blue-700">Phase</p>
                                    <p className="font-semibold text-blue-900">
                                        {getPhaseInfo(contractor.phase || 'trainee').name}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-blue-700">Hourly Rate</p>
                                    <p className="font-semibold text-blue-900">
                                        ${contractor.hourlyRate || 18}/hr
                                    </p>
                                </div>
                                <div>
                                    <p className="text-blue-700">Total Jobs</p>
                                    <p className="font-semibold text-blue-900">
                                        {contractor.metrics?.totalJobs || 0}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-blue-700">Member Since</p>
                                    <p className="font-semibold text-blue-900">
                                        {contractor.createdAt 
                                            ? new Date(contractor.createdAt).toLocaleDateString()
                                            : 'N/A'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
